// ── Tax Guard matching ────────────────────────────────────────────────────────
//
// Links each Tax Guard client to the ACE borrower it is actually about.
//
// Tax Guard monitors a TAXPAYER. ACE lends to an ACCOUNT, which can carry
// several — an operating company, a holding company, two guarantors. Until the
// two sides are joined nothing can be filed against a borrower, and a federal
// tax lien recorded against the wrong company is not a mistake that gets quietly
// corrected later. So the machine proposes and a person decides.
//
// ── WHY THERE IS NO "ACCEPT EVERYTHING" ─────────────────────────────────────
//
// There is "Accept exact EIN matches", and it does only that. An EIN match is an
// identity test — their number and ours are the same number — so accepting those
// in bulk asserts nothing a person would have to think about. The weaker
// suggestions sit one row at a time behind a dropdown, because "this account has
// only one borrower on it today" is a different kind of claim and deserves
// somebody's attention rather than a checkbox.
//
// No TIN or EIN reaches this page. The server does the comparison and sends back
// the last four and an entity id.
import { useState, useEffect, useMemo } from "react";
import { B } from "../../../shared/theme";
import { Btn, Card, ErrorMsg, STh, SearchBar, SectionHeader, Spinner, Td } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";
import { useSortable } from "../../../shared/hooks";

const REASONS = [
  ["not_funded",     "Never funded"],
  ["duplicate",      "Duplicate entry"],
  ["not_a_borrower", "Not our borrower"],
  ["closed",         "Closed / inactive"],
  ["other",          "Other"],
];
const REASON_LABEL = Object.fromEntries(REASONS);

// Mirrors authStateOf() in api/getTaxGuardMatching. The server decides the
// state so this page and the sync cannot disagree about what "active" means.
const AUTH = {
  active:   { label: "8821 active",   tone: "good", note: "A current Form 8821 is on file." },
  expiring: { label: "expires soon",  tone: "weak", note: "Authorization expires within 60 days." },
  expired:  { label: "8821 expired",  tone: "bad",  note: "The authorization has lapsed — this client is not being monitored." },
  rejected: { label: "8821 rejected", tone: "bad",  note: "The IRS rejected this authorization." },
  none:     { label: "no 8821",       tone: "bad",  note: "No authorization on file. Nothing is being monitored for this client." },
  unknown:  { label: "not checked",   tone: "flat", note: "We could not read the authorization for this client on this load." },
};

const HOW = {
  ein:            { label: "EIN match",    tone: "good", note: "Their EIN and the TIN on this entity are the same number." },
  "account+sole": { label: "Only entity",  tone: "weak", note: "referenceNumber found the account and it holds exactly one entity." },
  account:        { label: "Account only", tone: "weak", note: "referenceNumber found the account but not which entity on it." },
};

export const TaxGuardMatchingPage = () => {
  const { api, role } = useApp();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);
  const [picks, setPicks] = useState({});      // tg_client_id -> borrowerguarantorid ("" = clear)
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState("todo");
  const [q, setQ] = useState("");
  // Per row: the dropdown is scoped to the account referenceNumber names, and
  // this widens it back to everything. Scoping is right nearly always and
  // WRONG exactly when the reference is wrong — which is one of the cases this
  // page exists to fix. A filter with no way out would hide the right answer
  // precisely when you need it.
  const [wide, setWide] = useState({});
  // Pending dismissals, tg_client_id -> { reason, duplicate_of }. Staged like the
  // picks are, so one Save covers matches and dismissals together and nothing
  // happens on a stray click.
  const [drop, setDrop] = useState({});

  const load = () => {
    setLoading(true); setErr(null); setResult(null); setPicks({});
    api.get("getTaxGuardMatching")
      .then(d => {
        if (d && d.ok === false) setErr(d.error);
        else setData(d);
        setLoading(false);
      })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const entities = (data && data.entities) || [];
  const byId = useMemo(() => {
    const m = {};
    for (const e of entities) m[e.id] = e;
    return m;
  }, [entities]);

  // Grouped by account so the dropdown reads as "which borrower, on which deal"
  // rather than as 300 names in a row.
  const groupOf = (list) => {
    const g = [];
    let cur = null;
    for (const e of list) {
      if (!cur || cur.accountid !== e.accountid) { cur = { accountid: e.accountid, companyname: e.companyname, rows: [] }; g.push(cur); }
      cur.rows.push(e);
    }
    return g;
  };
  const grouped = useMemo(() => groupOf(entities), [entities]);
  const byAccount = useMemo(() => {
    const m = {};
    for (const e of entities) (m[e.accountid] = m[e.accountid] || []).push(e);
    return m;
  }, [entities]);

  // ── FLAT KEYS FOR SORTING ───────────────────────────────────────────────
  //
  // useSortable reads a single top-level property, and almost everything on
  // screen here is nested — r.auth.state, r.suggestion.how, r.mapped.name. So
  // each row carries a small set of derived columns whose only job is to be
  // sortable. They are prefixed to make clear they are not from the server.
  //
  // The 8821 sorts by SEVERITY rather than alphabetically. Sorting that column
  // is a question about which entries are broken, and "8821 active, 8821
  // expired, 8821 rejected, no 8821, not checked" in alphabetical order answers
  // nothing.
  const AUTH_RANK = { none: 0, rejected: 1, expired: 2, expiring: 3, unknown: 4, active: 5 };
  const HOW_RANK  = { ein: 0, "account+sole": 1, account: 2 };

  // Computed before the early returns, because hooks below must run on every
  // render. data is null until the first load lands, hence the guard.
  const rows = useMemo(() => ((data && data.clients) || []).map(r => ({
    ...r,
    _name:    r.name || "",
    _ref:     r.referenceNumber || "",
    _status:  r.status || "",
    _ein:     r.einlast4 || "",
    _auth:    AUTH_RANK[(r.auth || {}).state] ?? 9,
    _authto:  (r.auth || {}).authTo || "",
    _authfrom:(r.auth || {}).authFrom || "",
    _match:   r.affiliate ? "◇ " + (r.affiliate.companyname || "")
              : r.mapped ? r.mapped.borrowerguarantorname || ""
              : r.suggestion ? r.suggestion.borrowerguarantorname || r.suggestion.companyname || "" : "",
    _sugg:    r.dismissal ? 9 : r.mapped ? -1 : r.affiliate ? -1 : r.accountOnly ? 3 : (HOW_RANK[(r.suggestion || {}).how] ?? 8),
    _dupes:   (r.sameEin || []).length,
  })), [data]);

  // Search runs over the SAME strings the table renders, so a search that finds
  // nothing means it is not on screen — rather than it being on screen under a
  // field the search happens not to cover.
  const needle = q.trim().toLowerCase();
  const searched = useMemo(() => !needle ? rows : rows.filter(r =>
    [r._name, r._ref, r._status, r._ein, r._match, r.tg_client_id,
     (r.refAccount || {}).companyname, (r.refAccount || {}).accountnumber,
     (r.auth || {}).authTo, (r.auth || {}).authFrom, ...(r.sameEin || [])]
      .some(v => v && String(v).toLowerCase().includes(needle))), [rows, needle]);

  const { sorted, sortKey, sortDir, toggle } = useSortable(searched, "_name");

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;
  if (!data) return null;

  const clients = rows;
  // Lookup for the duplicate line, so a row can report its twin's 8821 state.
  const byTg = {};
  for (const x of clients) byTg[x.tg_client_id] = x;
  const c = data.counts || {};

  // What a row will be saved as: an explicit pick wins, otherwise the suggestion,
  // and an existing mapping is left alone unless touched.
  // ── THE SELECT HOLDS TWO KINDS OF ANSWER ──
  // An entity id, or "aff:<accountid>" meaning "monitored under this account but
  // not a borrower on it". One control, because from the user's side it is one
  // question — what IS this Tax Guard client to us — and splitting it across a
  // dropdown and a checkbox would let them disagree.
  const AFF = "aff:";
  const affAcct = v => (typeof v === "string" && v.startsWith(AFF) ? v.slice(AFF.length) : null);

  const chosen = r => {
    if (Object.prototype.hasOwnProperty.call(picks, r.tg_client_id)) return picks[r.tg_client_id];
    if (r.affiliate) return AFF + r.affiliate.accountid;
    if (r.mapped) return r.mapped.borrowerguarantorid || "";
    // A row linked to an account but to no borrower was deliberately unlinked —
    // that is the only way it gets into this state. Re-proposing the suggestion
    // here would undo the decision on the next Save, and would leave the page
    // showing pending changes the moment it loads. Leave it blank and let the
    // chip ask for a borrower.
    if (r.accountOnly) return "";
    if (r.suggestion && r.suggestion.borrowerguarantorid) return r.suggestion.borrowerguarantorid;
    return "";
  };
  const isDirty = r => {
    const now = chosen(r);
    const was = r.affiliate ? AFF + r.affiliate.accountid
              : r.mapped ? (r.mapped.borrowerguarantorid || "")
              : r.accountOnly ? ""          // already unlinked; blank is not a change
              : null;
    return was === null ? !!now : now !== was;
  };

  const isDropped = r => Object.prototype.hasOwnProperty.call(drop, r.tg_client_id)
    ? !!drop[r.tg_client_id]
    : !!r.dismissal;

  // ── ONE PREDICATE FOR THE TAB AND ITS COUNT ─────────────────────────────
  //
  // These used to be two separate pieces of logic: the rows came from a filter
  // here, the number in the tab came from a count computed on the server. They
  // drifted the moment affiliates existed — the server still counted an
  // affiliate as outstanding work while this filter correctly hid it, so the tab
  // read "Needs matching (1)" above an empty table.
  //
  // A count that can disagree with the list it labels is worse than no count:
  // the list is right, so the number teaches you to distrust the screen. Both
  // now come from inView(), and a future state can only be added in one place.
  const inView = (r, key) => {
    if (key === "all") return true;
    if (key === "dismissed") return isDropped(r);
    // Every other view is a to-do view, and a dismissed client is by definition
    // not a to-do.
    if (isDropped(r)) return false;
    if (key === "todo")      return !r.mapped && !r.affiliate;
    if (key === "mapped")    return !!r.mapped;
    if (key === "affiliate") return !!r.affiliate;
    if (key === "none")      return !r.mapped && !r.affiliate && !r.suggestion;
    if (key === "dupes")     return !!(r.sameEin && r.sameEin.length);
    if (key === "noauth")    return !r.mapped && !r.affiliate
                                  && ["none", "rejected", "expired"].includes((r.auth || {}).state);
    return true;
  };

  const shown = sorted.filter(r => inView(r, filter));
  // Counted over the SEARCHED set, so the number is always what the tab will
  // show. The alternative — counting the whole book — puts the mismatch back the
  // moment anybody types in the search box.
  const countIn = key => searched.filter(r => inView(r, key)).length;

  // A row is pending if its match changed OR its dismissal state did.
  const dropChanged = r => isDropped(r) !== !!r.dismissal
    || (isDropped(r) && r.dismissal && drop[r.tg_client_id]
        && drop[r.tg_client_id].reason !== r.dismissal.reason);
  const pending = clients.filter(r => (!isDropped(r) && isDirty(r)) || dropChanged(r));

  const acceptExact = () => {
    const next = { ...picks };
    for (const r of clients) {
      if (!r.mapped && r.suggestion && r.suggestion.how === "ein" && r.suggestion.borrowerguarantorid) {
        next[r.tg_client_id] = r.suggestion.borrowerguarantorid;
      }
    }
    setPicks(next);
  };

  // Bulk only for the one unambiguous pile: unmatched clients Tax Guard
  // itself reports as CLOSED or INACTIVE. Reversible, and it does not touch
  // anything ACTIVE — the judgement being made is "Tax Guard says this is
  // over", not "I think this one does not matter".
  const dismissInactive = () => {
    const next = { ...drop };
    for (const r of clients) {
      if (r.mapped || r.dismissal) continue;
      if (["CLOSED", "INACTIVE"].includes(String(r.status || "").toUpperCase())) {
        next[r.tg_client_id] = { reason: "closed" };
      }
    }
    setDrop(next);
  };

  const save = async () => {
    setSaving(true); setResult(null);
    try {
      const mappings = [], unmap = [], dismiss = [], undismiss = [];
      for (const r of pending) {
        if (dropChanged(r)) {
          if (isDropped(r)) {
            const d = drop[r.tg_client_id] || {};
            dismiss.push({ tg_client_id: r.tg_client_id, tg_client_name: r.name,
                           reason: d.reason || "other", duplicate_of: d.duplicate_of || null });
          } else {
            undismiss.push(r.tg_client_id);
          }
          continue;
        }
        const ent = chosen(r);
        if (!ent) { if (r.mapped || r.affiliate) unmap.push(r.tg_client_id); continue; }

        const common = {
          tg_client_id: r.tg_client_id, tg_client_url: r.tg_client_url,
          tg_reference_number: r.referenceNumber, tg_cost_center: r.costCenter,
          tg_status: r.status, monitor_type: r.monitor_type, name: r.name,
        };

        const aAcct = affAcct(ent);
        if (aAcct) {
          mappings.push({ ...common, accountid: aAcct, borrowerguarantorid: null,
                          is_affiliate: true });
          continue;
        }
        const e = byId[ent];
        if (!e) continue;
        mappings.push({ ...common, accountid: e.accountid, borrowerguarantorid: e.id });
      }
      setResult(await api.post("saveTaxGuardMapping", { mappings, unmap, dismiss, undismiss }));
      load();
    } catch (e) { setResult({ ok: false, error: e.message }); }
    setSaving(false);
  };

  // "bad" was added for the 8821 column. Without it an expired or missing
  // authorization would render in the same neutral grey as "not checked" — and
  // telling those two apart is the entire reason the column exists.
  const TONE = {
    good: { bg: "#E1F5EE", fg: "#0F6E56" },
    weak: { bg: "#FAEEDA", fg: "#7a5200" },
    bad:  { bg: "#FBE4DD", fg: "#993C1D" },
    flat: { bg: "#F0F0EF", fg: "var(--color-text-secondary)" },
  };
  const chip = (text, tone, title) => {
    const t = TONE[tone] || TONE.flat;
    return <span title={title} style={{
      fontSize: 11, padding: "2px 8px", borderRadius: 4, fontWeight: 600, whiteSpace: "nowrap",
      background: t.bg, color: t.fg,
    }}>{text}</span>;
  };

  const TAB = [
    ["todo",      "Needs matching"],
    ["mapped",    "Matched"],
    ["affiliate", "Affiliates"],
    ["dupes",     "Same EIN"],
    ["noauth",    "No live 8821"],
    ["none",      "No suggestion"],
    ["dismissed", "Dismissed"],
    ["all",       "All"],
  ].map(([k, label]) => [k, label, countIn(k)]);

  return <>
    <SectionHeader title="Tax Guard matching" count={c.total} />

    <Card style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 12.5, lineHeight: 1.7, color: "var(--color-text-secondary)", marginBottom: 12 }}>
        <strong style={{ color: "var(--color-text-primary)" }}>{data.label}</strong> ·{" "}
        <strong>{c.total}</strong> Tax Guard client{c.total === 1 ? "" : "s"} ·{" "}
        <strong style={{ color: "#0F6E56" }}>{c.mapped}</strong> matched ·{" "}
        <strong>{c.exact}</strong> exact EIN match{c.exact === 1 ? "" : "es"} waiting ·{" "}
        <strong>{c.weak}</strong> weaker suggestion{c.weak === 1 ? "" : "s"} ·{" "}
        <strong>{c.none}</strong> with nothing to go on
        {!!c.affiliates && <> · <strong>{c.affiliates}</strong> affiliate{c.affiliates === 1 ? "" : "s"}</>}
        {!!c.accountOnly && <> · <strong style={{ color: "#7a5200" }}>{c.accountOnly}</strong> linked to an account but no borrower</>}
        {!!c.dismissed && <> · <strong>{c.dismissed}</strong> dismissed</>}
        {!!c.duplicates && <> · <strong style={{ color: "#7a5200" }}>{c.duplicates}</strong> sharing an EIN with another entry</>}.
        {data.truncated && <> Only the first page was read — there are more than this list shows.</>}
        {!!data.authTruncated && (
          <div style={{ marginTop: 6, color: "#7a5200" }}>
            {data.authTruncated} authorization{data.authTruncated === 1 ? " was" : "s were"} not read before the
            time budget ran out — they show as &ldquo;not checked&rdquo;, which is not the same as having none.
            Reload to pick them up.
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {TAB.map(([k, label, n]) => (
          <span key={k} onClick={() => setFilter(k)}
                style={{ cursor: "pointer", fontSize: 12.5, padding: "4px 10px", borderRadius: 6,
                         fontWeight: filter === k ? 600 : 400,
                         background: filter === k ? B.cyanLight : "transparent",
                         color: filter === k ? B.cyanDark : "var(--color-text-secondary)" }}>
            {label} ({n || 0})
          </span>
        ))}
        <span style={{ flex: 1 }} />
        <SearchBar value={q} onChange={setQ} placeholder="Name, EIN last 4, reference, account…" />
        {!!needle && (
          <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            {shown.length} of {clients.length}
          </span>
        )}
        {!!c.inactive && role.canEdit && (
          <Btn onClick={dismissInactive}
               title="Selects every unmatched client Tax Guard reports as CLOSED or INACTIVE. Nothing is saved until you press Save, and a dismissal can be undone.">
            Dismiss {c.inactive} closed / inactive
          </Btn>
        )}
        {!!c.exact && role.canEdit && (
          <Btn onClick={acceptExact} title="Selects every exact EIN match. Nothing is saved until you press Save.">
            Accept {c.exact} exact EIN match{c.exact === 1 ? "" : "es"}
          </Btn>
        )}
        {role.canEdit && (
          <Btn variant="primary" disabled={saving || !pending.length} onClick={save}>
            {saving ? "Saving…" : pending.length ? "Save " + pending.length + " change" + (pending.length === 1 ? "" : "s") : "Save"}
          </Btn>
        )}
      </div>

      {result && (
        <div style={{ marginTop: 12, fontSize: 12.5, lineHeight: 1.6, borderRadius: 8, padding: "8px 12px",
                      background: result.ok === false ? "#FBE4DD" : "#E1F5EE",
                      color: result.ok === false ? "#993C1D" : "#0F6E56" }}>
          {result.error ? result.error : <>
            {result.linked} linked · {result.updated} updated · {result.unlinked} unlinked
            {!!result.dismissed && <> · {result.dismissed} dismissed</>}
            {!!result.restored && <> · {result.restored} restored</>}
            {!!result.rejected && <strong> · {result.rejected} rejected</strong>}
            {(result.results || []).filter(x => !x.ok).map((x, i) => (
              <div key={i} style={{ color: "#993C1D", marginTop: 4 }}>{x.name || x.tg_client_id}: {x.error}</div>
            ))}
          </>}
        </div>
      )}
    </Card>

    <Card style={{ padding: 0, overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>
          {[["_name",     "Tax Guard client"],
            ["_ein",      "EIN"],
            ["_ref",      "referenceNumber"],
            ["_auth",     "8821"],
            ["_authfrom", "Authorized from"],
            ["_sugg",     "Suggestion"],
            ["_match",    "ACE borrower / guarantor"]].map(([k, label]) => (
            <STh key={k} label={label} sortKey={k} currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          ))}
        </tr></thead>
        <tbody>
          {shown.map(r => {
            const sel = chosen(r);
            const dirty = isDirty(r);
            // Scope to the account referenceNumber names — unless there is no such
            // account, or the row was widened, or the entity already chosen lives
            // somewhere else. That last case matters: a row mapped to another
            // account would otherwise render with a value its own list does not
            // contain, which browsers display as blank and read as data loss.
            const dropped = isDropped(r);
            const selAcct = affAcct(sel) || (byId[sel] || {}).accountid;
            const selElsewhere = !!(sel && selAcct && r.refAccount && selAcct !== r.refAccount.accountid);
            const scoped = !!r.refAccount && !wide[r.tg_client_id] && !selElsewhere;
            const opts = scoped ? groupOf(byAccount[r.refAccount.accountid] || []) : grouped;
            const sug = r.suggestion;
            const h = sug ? HOW[sug.how] : null;
            return (
              <tr key={r.tg_client_id} style={{ background: dropped ? "#FAFAF9" : dirty ? "#FFFDF5" : undefined,
                                                opacity: dropped ? 0.6 : 1 }}>
                <Td>
                  <div style={{ fontWeight: 500 }}>{r.name || "(no name)"}</div>
                  <div style={{ fontSize: 11, color: "var(--color-text-secondary)", fontFamily: "var(--font-mono)" }}>
                    #{r.tg_client_id}{r.status ? " · " + r.status : ""}{r.monitor_type ? " · " + r.monitor_type : ""}
                  </div>
                  {/* Same EIN as another entry. The usual cause is an 8821 that
                      failed to submit, so a second client was created to get a
                      working one — leaving a dead twin. Shown rather than
                      auto-resolved: which of the pair is the live one is a look
                      at the authorization, not something to infer from a date. */}
                  {!!(r.sameEin || []).length && (
                    <div style={{ fontSize: 11, marginTop: 3, color: "#7a5200" }}>
                      Same EIN as {r.sameEin.map((id, i) => {
                        // The twin's authorization, named next to it. This is the
                        // answer to "which of these two is the real one" — the
                        // entry holding a live 8821 is the one doing the work,
                        // and the other is the failed submission it replaced.
                        const o = byTg[id];
                        const st = o && o.auth ? (AUTH[o.auth.state] || AUTH.unknown).label : "?";
                        return <span key={id}>{i ? ", " : ""}#{id} ({st})</span>;
                      })}
                    </div>
                  )}
                </Td>
                <Td style={{ fontFamily: "var(--font-mono)", fontSize: 12, whiteSpace: "nowrap" }}>
                  {r.einlast4 ? (r.isSsn ? "SSN ••••" : "••••") + r.einlast4
                             : <span style={{ color: "var(--color-text-secondary)" }}>none</span>}
                </Td>
                <Td style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
                  {r.referenceNumber || <span style={{ color: "var(--color-text-secondary)", fontFamily: "inherit" }}>blank</span>}
                </Td>
                <Td style={{ whiteSpace: "nowrap" }}>
                  {(() => {
                    const a = AUTH[(r.auth || {}).state] || AUTH.unknown;
                    return <>
                      {chip(a.label, a.tone, a.note)}
                      {r.auth && r.auth.authTo && (
                        <div style={{ fontSize: 10.5, color: "var(--color-text-secondary)",
                                      fontFamily: "var(--font-mono)", marginTop: 3 }}>
                          to {r.auth.authTo}
                        </div>
                      )}
                      {r.auth && r.auth.rejectedOn && (
                        <div style={{ fontSize: 10.5, color: "#993C1D", marginTop: 3 }}>
                          rejected {r.auth.rejectedOn}
                        </div>
                      )}
                    </>;
                  })()}
                </Td>
                {/* ── "Authorized from", NOT "uploaded" ──
                    Tax Guard's portal shows an upload date; their API v2.14 does
                    not return one. The 8821 resource has exactly four fields —
                    authFrom, authTo, rejectDate, rejectReasons — so a column
                    labelled "uploaded" here would be a date we invented from the
                    nearest thing to hand, which is how a number nobody can
                    reproduce ends up in a credit file.
                    authFrom is the real field and answers the question that
                    matters for duplicates: of two entries for one taxpayer, the
                    later authorization is the live resubmission. */}
                <Td style={{ fontFamily: "var(--font-mono)", fontSize: 12, whiteSpace: "nowrap" }}>
                  {(r.auth || {}).authFrom
                    || <span style={{ color: "var(--color-text-secondary)", fontFamily: "inherit" }}>—</span>}
                </Td>
                <Td>
                  {/* ── THIS COLUMN FOLLOWS THE DROPDOWN, NOT THE DATABASE ──
                      It used to read r.mapped, which is the SAVED state, while
                      the dropdown beside it showed the PENDING one. Set a
                      matched row back to "not matched" and the two cells
                      disagreed — the chip still said "matched" — until a save
                      and reload. Two controls on one row describing the same
                      fact differently is not a cosmetic problem: it makes the
                      screen untrustworthy, and the honest reading of it ("my
                      change did not take") is the one that looks right.

                      So the chip now says what Save will DO. */}
                  {(() => {
                    if (dropped) return chip("will dismiss", "weak", "Pending — this client will be dismissed on Save.");
                    if (dirty) {
                      const now = chosen(r);
                      if (!now) return chip("will unlink", "bad",
                        "Pending — the existing link is removed on Save.");
                      const a = affAcct(now);
                      if (a) return chip("will mark affiliate", "weak",
                        "Pending — recorded as monitored under this account without being a "
                        + "borrower or guarantor on it.");
                      return chip(r.mapped ? "will move" : "will match", "weak",
                        "Pending — " + ((byId[now] || {}).borrowerguarantorname || "this borrower")
                        + " is linked on Save.");
                    }
                    if (r.mapped) return chip("matched", "good", "Linked. Change the dropdown to move it.");
                    // Settled, not outstanding. Findings are recorded against the
                    // account, which is the relationship they actually bear on.
                    if (r.affiliate) return chip("affiliate", "good",
                      "Monitored under " + (r.affiliate.companyname || r.affiliate.accountid)
                      + " without being a borrower or guarantor on it."
                      + (r.affiliate.note ? " — " + r.affiliate.note : ""));
                    // Pinned to an account with no borrower on it. Findings cannot
                    // be attributed to a taxpayer from here, so it is work, not a
                    // finished match — and it is what an unmatched row looks like
                    // after its link was cleared.
                    if (r.accountOnly) return chip("pick a borrower", "weak",
                      "Linked to " + (r.accountOnly.companyname || r.accountOnly.accountid)
                      + " but to no borrower on it. Nothing can be attributed to a taxpayer until one is chosen.");
                    if (h) return chip(h.label, h.tone, h.note);
                    return chip("none", "flat",
                      "Nothing to go on: no EIN we hold, and referenceNumber is not an ACE account number.");
                  })()}
                </Td>
                <Td>
                  <select value={sel} disabled={!role.canEdit}
                          onChange={e => setPicks(p => ({ ...p, [r.tg_client_id]: e.target.value }))}
                          style={{ width: "100%", padding: "6px 8px", fontSize: 12.5, borderRadius: 6,
                                   border: "1px solid " + (dirty ? B.cyanDark : "#ccc"),
                                   background: "var(--color-background-primary)" }}>
                    <option value="">— not matched —</option>
                    {opts.map(g => (
                      <optgroup key={g.accountid} label={g.companyname + " (" + g.accountid + ")"}>
                        {/* Offered on every account, because an affiliate is a
                            relationship to the ACCOUNT and there is no borrower
                            row to hang it on. */}
                        <option value={AFF + g.accountid}>
                          ◇ affiliate of {g.companyname} — not a borrower or guarantor
                        </option>
                        {g.rows.map(e => (
                          <option key={e.id} value={e.id}>
                            {e.borrowerguarantorname}
                            {e.entitytype ? " · " + e.entitytype : ""}
                            {e.tinlast4 ? " · TIN ••••" + e.tinlast4 : " · no TIN on file"}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>

                  <div style={{ fontSize: 11, marginTop: 4, color: "var(--color-text-secondary)" }}>
                    {scoped ? <>
                      Showing the {r.refAccount.entities} borrower{r.refAccount.entities === 1 ? "" : "s"} on{" "}
                      <strong>{r.refAccount.companyname}</strong>
                      {r.refAccount.accountnumber ? " (#" + r.refAccount.accountnumber + ")" : ""}.{" "}
                      <span onClick={() => setWide(w => ({ ...w, [r.tg_client_id]: true }))}
                            style={{ cursor: "pointer", color: B.cyanDark, textDecoration: "underline",
                                     textDecorationStyle: "dotted" }}>
                        Show every account
                      </span>
                    </> : r.refAccount ? <>
                      Showing every account.{" "}
                      <span onClick={() => setWide(w => ({ ...w, [r.tg_client_id]: false }))}
                            style={{ cursor: "pointer", color: B.cyanDark, textDecoration: "underline",
                                     textDecorationStyle: "dotted" }}>
                        Limit to {r.refAccount.companyname}
                      </span>
                    </> : (
                      /* No account to scope to. Saying why beats a silently
                         unfiltered list that looks like a mistake. */
                      r.referenceNumber
                        ? <>No ACE account has the account number &ldquo;{r.referenceNumber}&rdquo;, so every account is listed.</>
                        : <>referenceNumber is blank, so every account is listed.</>
                    )}
                  </div>

                  {/* An account NUMBER that is not unique means the reference does
                      not identify one account, however well-formed it looks. Said
                      on the row because the scoped list below it is then showing
                      one of several possible accounts. */}
                  {r.refAccount && r.refAccount.dupes > 1 && (
                    <div style={{ fontSize: 11, marginTop: 4, color: "#993C1D", background: "#FBE4DD",
                                  border: "0.5px solid #F0C8BB", borderRadius: 6, padding: "4px 8px" }}>
                      Account number {r.referenceNumber} belongs to {r.refAccount.dupes} ACE accounts.
                      This is showing one of them — check it is the right one, or widen the list.
                    </div>
                  )}

                  {/* ── Dismissing ──
                      Staged, not immediate: one Save covers matches and
                      dismissals together, and nothing is lost to a stray click.
                      Reversible from the Dismissed tab, and the row is kept in
                      the database either way. */}
                  {role.canEdit && (
                    <div style={{ fontSize: 11, marginTop: 6 }}>
                      {dropped ? (
                        <span style={{ color: "#7a5200" }}>
                          Dismissed — {REASON_LABEL[(drop[r.tg_client_id] || r.dismissal || {}).reason] || "Other"}
                          {" · "}
                          <span onClick={() => setDrop(d => ({ ...d, [r.tg_client_id]: false }))}
                                style={{ cursor: "pointer", color: B.cyanDark, textDecoration: "underline",
                                         textDecorationStyle: "dotted" }}>
                            Put back on the list
                          </span>
                        </span>
                      ) : (
                        <select value=""
                                onChange={e => e.target.value && setDrop(d => ({ ...d, [r.tg_client_id]: { reason: e.target.value } }))}
                                style={{ fontSize: 11, padding: "2px 6px", borderRadius: 5,
                                         border: "1px solid #ddd", color: "var(--color-text-secondary)",
                                         background: "transparent" }}>
                          <option value="">Dismiss as…</option>
                          {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      )}
                    </div>
                  )}

                  {/* Both cannot be true. Shown on the row rather than counted in
                      a summary, because the fix is a judgement about THIS pair. */}
                  {r.refConflict && (
                    <div style={{ fontSize: 11, marginTop: 4, color: "#7a5200", background: "#FAEEDA",
                                  border: "0.5px solid #EBD3A0", borderRadius: 6, padding: "4px 8px" }}>
                      The EIN matches a borrower on <strong>{r.suggestion.companyname}</strong>, but
                      referenceNumber says <strong>{r.refAccount.companyname}</strong>. Either the
                      reference is wrong or the TIN is on the wrong entity in ACE.
                    </div>
                  )}
                </Td>
              </tr>
            );
          })}
          {!shown.length && (
            <tr><Td colSpan={7} style={{ color: "var(--color-text-secondary)", padding: 24, textAlign: "center" }}>
              {needle ? <>Nothing matches &ldquo;{q}&rdquo; in this view. Try the All tab.</>
                      : <>Nothing in this view.</>}
            </Td></tr>
          )}
        </tbody>
      </table>
    </Card>

    <Card style={{ padding: 16, marginTop: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>How the matching works</div>
      <div style={{ fontSize: 12.5, color: "var(--color-text-secondary)", lineHeight: 1.7 }}>
        <strong>EIN match</strong> — their EIN and the TIN recorded against that entity are the same
        number. That is an identity test, not a resemblance, and it is the only suggestion offered in
        bulk.
        <br />
        <strong>Only entity</strong> — <code>referenceNumber</code> found the ACE account and that
        account holds exactly one borrower or guarantor, so there is nothing to disambiguate. Correct
        today; it quietly becomes a guess the day a second entity is added, which is why it is not
        accepted in bulk.
        <br />
        <strong>Account only</strong> — the account was found but not which taxpayer on it. Pick one.
        <br />
        <strong>None</strong> — no EIN we hold, and <code>referenceNumber</code> is not an ACE account
        number. Setting <code>referenceNumber</code> to the account number in Tax Guard's portal fixes
        these for next time; matching here fixes them now and for good, because the sync reads this
        table first.
        <br /><br />
        <strong>Affiliate</strong> — a Tax Guard client monitored under an account without being a
        borrower or guarantor on it, such as an operating company under common ownership. Pick it from
        the top of any account&rsquo;s group in the dropdown. Findings are recorded against the account,
        and the row stops asking to be matched because having no borrower is the answer rather than a
        gap.
        <br /><br />
        Once a row is matched the sync no longer depends on <code>referenceNumber</code> being right.
        No taxpayer identification number is sent to this page — the comparison happens on the server.
      </div>
    </Card>
  </>;
};
